import { useEffect, useState } from 'react'
import { api } from '../services'
import { UserUnitSettings } from '../common/constants'
import { LoadingSpinner } from '../shared/components/ui/LoadingSpinner'
import Button from '../shared/components/ui/Button'
import { useAccountContext } from '../contexts'

// Clarify the two easily-confused volume categories. Keyed by the numeric
// UnitCategoryId as a string (1 = Volume, 10 = Total Volume).
const CATEGORY_HELP: Record<string, string> = {
  '1': "Unit for each individual reading's volume (per reading interval), e.g. gallons. Shown as the blue bars on flow charts.",
  '10': 'Unit for the cumulative running total, e.g. acre-ft. Shown as the Flow Total on flow charts.',
}

export function UnitPreferencesTab() {
  const { setUserUnitSettings: updateUserUnitSettings } = useAccountContext()

  const [userUnitSettingsOriginal, setUserUnitSettingsOriginal] = useState<UserUnitSettings>({})
  const [userUnitSettings, setUserUnitSettings] = useState<UserUnitSettings>({})
  const [selections, setSelections] = useState<Record<string, any>>({})
  const [dictionary, setDictionary] = useState<Record<string, string>>({})
  const [hasChanged, setHasChanged] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    api
      .getUserUnits()
      .then((result) => {
        setUserUnitSettingsOriginal(result.data.userUnitSettings)
        setUserUnitSettings(result.data.userUnitSettings)
        setSelections(result.data.selections)
        setDictionary(result.data.dictionary)
      })
      .catch((error) => {
        console.error('api.getUserUnits()', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    setHasChanged(Object.keys(difference()).length > 0)
  }, [userUnitSettings, userUnitSettingsOriginal]) // eslint-disable-line react-hooks/exhaustive-deps

  function updateUnits(unitCategoryId: string, unitId: number) {
    setUserUnitSettings({
      ...userUnitSettings,
      [unitCategoryId]: {
        ...userUnitSettings[unitCategoryId],
        unitId,
      },
    })
  }

  function updatePrecision(unitCategoryId: string, precision: number) {
    setUserUnitSettings({
      ...userUnitSettings,
      [unitCategoryId]: {
        ...userUnitSettings[unitCategoryId],
        precision,
      },
    })
  }

  function reset() {
    setUserUnitSettings(userUnitSettingsOriginal)
  }

  function save() {
    if (hasChanged) {
      setIsUpdating(true)

      api
        .updateUserUnitSettings(difference())
        .then((result) => {
          setUserUnitSettingsOriginal(result.data.current)
          setUserUnitSettings(result.data.current)
          updateUserUnitSettings(result.data.current)
        })
        .catch((error) => {
          console.error('api.updateUserUnitsSettings()', { error })
        })
        .finally(() => {
          setIsUpdating(false)
        })
    }
  }

  function difference() {
    const differences: Record<string, any> = {}

    for (const unitCategoryId in userUnitSettings) {
      if (userUnitSettings.hasOwnProperty(unitCategoryId)) {
        const originalUnit = userUnitSettingsOriginal[unitCategoryId]
        const currentUnit = userUnitSettings[unitCategoryId]

        if (
          originalUnit === undefined ||
          originalUnit.unitId !== currentUnit.unitId ||
          originalUnit.precision !== currentUnit.precision
        ) {
          if (originalUnit === undefined) {
            differences[unitCategoryId] = { ...currentUnit, unitId: 65 }
          } else {
            differences[unitCategoryId] = currentUnit
          }
        }
      }
    }

    return differences
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Unit Preferences</h3>
          <p className="mt-1 text-sm text-gray-600">
            Configure your preferred units and precision for different measurement categories
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {isUpdating && (
            <div className="flex items-center text-sm text-gray-600">
              <LoadingSpinner size="sm" className="mr-2" />
              Saving...
            </div>
          )}
          <Button variant="outline" size="sm" onClick={reset} disabled={!hasChanged || isUpdating}>
            Reset
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={!hasChanged} loading={isUpdating}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Unit Preferences - Card Based Design */}
      <div className="space-y-4">
        {/* Column Headers */}
        <div className="grid grid-cols-3 gap-6 px-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Category</div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Precision</div>
        </div>

        {/* Unit Category Cards */}
        {Object.keys(dictionary).map((unitCategoryId: string) => (
          <div key={unitCategoryId} className="grid grid-cols-3 gap-6 items-center p-4 bg-white rounded-lg border border-gray-200">
            {/* Category Name Column */}
            <div className="flex items-center gap-1.5">
              <h4 className="text-sm font-medium text-gray-900">{dictionary[unitCategoryId]}</h4>
              {CATEGORY_HELP[unitCategoryId] && (
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-semibold cursor-help"
                  title={CATEGORY_HELP[unitCategoryId]}
                  aria-label={CATEGORY_HELP[unitCategoryId]}
                >
                  ?
                </span>
              )}
            </div>

            {/* Unit Selection Column */}
            <div>
              <select
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={userUnitSettings[unitCategoryId] ? userUnitSettings[unitCategoryId].unitId : undefined}
                onChange={(e) => {
                  updateUnits(unitCategoryId, parseInt(e.target.value))
                }}
              >
                {selections[unitCategoryId] &&
                  selections[unitCategoryId].map((option: any, idx: number) => {
                    return (
                      <option key={idx} value={option.unitId}>
                        {option.shortName}
                      </option>
                    )
                  })}
              </select>
            </div>

            {/* Precision Selection Column */}
            <div>
              <select
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={userUnitSettings[unitCategoryId] ? userUnitSettings[unitCategoryId].precision : 0}
                onChange={(e) => {
                  updatePrecision(unitCategoryId, parseInt(e.target.value))
                }}
              >
                <option value={0}>0 decimal places</option>
                <option value={1}>1 decimal place</option>
                <option value={2}>2 decimal places</option>
                <option value={3}>3 decimal places</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Unit Preview Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path
                fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">Unit Preview</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p className="mb-2">Sample values using your selected units:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                <div>
                  <span className="font-medium">Temperature:</span> 22.5°C
                </div>
                <div>
                  <span className="font-medium">Pressure:</span> 1013.25 hPa
                </div>
                <div>
                  <span className="font-medium">Distance:</span> 1.50 m
                </div>
                <div>
                  <span className="font-medium">Volume:</span> 125.0 L
                </div>
                <div>
                  <span className="font-medium">Flow:</span> 15.3 L/min
                </div>
                <div>
                  <span className="font-medium">Weight:</span> 2.3 kg
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-yellow-800">Unit Preferences Help</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong>Units:</strong> Choose your preferred units for each measurement category (metric or
                  imperial)
                </li>
                <li>
                  <strong>Precision:</strong> Set the number of decimal places (0-3) for displaying values
                </li>
                <li>Changes apply to all charts, tables, and data displays throughout the application</li>
                <li>Unit conversions are performed automatically using built-in formulas</li>
                <li>Click "Save Changes" to apply your preferences, or "Reset" to revert unsaved changes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
